import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  ARRANGEMENT_FEE,
  GUARANTEE_RATE,
  HOME_INSURANCE,
  INSURANCE_BANK_GROUP,
  PROPERTY_TAX_PER_M2,
  SERVICE_CHARGE_PER_M2,
} from "../lib/financeDefaults";
import type { InsuranceBasis, PropertyKind } from "../lib/finance";

/**
 * Your financial situation, not the property's.
 *
 * Deliberately global rather than per-scenario: a buyer has one deposit, one
 * income and one set of bank terms, and tries them against many flats. The
 * price comes from whichever area and scenario is open, so this holds only the
 * things that stay the same as you look around.
 */
interface FinanceState {
  /** "estimate" prices the active scenario's flat; "manual" uses your own figure. */
  priceMode: "estimate" | "manual";
  manualPrice: number;
  agencyFees: number;
  works: number;
  kind: PropertyKind;
  firstTimeBuyer: boolean;

  deposit: number;
  years: number;
  /** "market" follows the dated broker averages for the term you pick. */
  rateMode: "market" | "manual";
  manualRate: number;
  insuranceRate: number;
  insuranceBasis: InsuranceBasis;
  /** 1 for a single borrower, 2 for a couple each covered at 100%. */
  quotity: number;
  arrangementFee: number;
  guaranteeRate: number;
  brokerFee: number;

  /**
   * "commune" takes the rate the selected commune actually votes; "manual"
   * uses your own figure, which is what the seller's tax notice gives you.
   */
  propertyTaxMode: "commune" | "manual";
  /** Per m² per year, used in manual mode; multiplied by the flat's size. */
  propertyTaxPerM2: number;
  serviceChargePerM2: number;
  homeInsurance: number;

  /** Net monthly household income. 0 means "not given" — no ratio is shown. */
  income: number;

  set: <K extends keyof FinanceState>(key: K, value: FinanceState[K]) => void;
  reset: () => void;
}

const DEFAULTS = {
  priceMode: "estimate" as const,
  manualPrice: 350_000,
  agencyFees: 0,
  works: 0,
  kind: "ancien" as PropertyKind,
  firstTimeBuyer: false,

  deposit: 60_000,
  years: 20,
  rateMode: "market" as const,
  manualRate: 0.033,
  insuranceRate: INSURANCE_BANK_GROUP,
  insuranceBasis: "initial" as InsuranceBasis,
  quotity: 1,
  arrangementFee: ARRANGEMENT_FEE,
  guaranteeRate: GUARANTEE_RATE,
  brokerFee: 0,

  propertyTaxMode: "commune" as const,
  propertyTaxPerM2: PROPERTY_TAX_PER_M2,
  serviceChargePerM2: SERVICE_CHARGE_PER_M2,
  homeInsurance: HOME_INSURANCE,

  income: 0,
};

export const useFinanceStore = create<FinanceState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      set: (key, value) => set({ [key]: value } as never),
      reset: () => set(DEFAULTS),
    }),
    { name: "mete-finance" },
  ),
);
